import CommonModal from 'components/common/CommonModal';
import DefaultButton from 'components/common/DefaultButton';
import DefaultImageView from 'components/common/DefaultImageView';
import DefaultText from 'components/common/DefaultText';
import LabelInput from 'components/common/LabelInput';
import {changeBusinessRegisterNumber} from 'function/api';
import React, {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';

type Props = {
  selectItem: any;
  entityType: 'contractors' | 'partner-companies';
  closeModal: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
};

const ChangeBusinessRegisterNumberModal = ({
  selectItem,
  entityType,
  closeModal,
  setIsLoading,
  setErrorModal,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);
  const [bizRegNumber, setBizRegNumber] = useState<string>('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);

  useEffect(() => {
    if (selectItem) {
      setBizRegNumber(selectItem?.businessRegisterNumber || '');
    }
  }, [selectItem]);

  const entityLabel = entityType === 'contractors' ? '시공사' : '협력사';

  return (
    <>
      <CommonModal closeModal={closeModal}>
        <ModalContainer>
          <ModalHeader>
            <DefaultText
              text={`${entityLabel} 사업자등록번호 변경`}
              textStyle={{fontSize: minmax(40)}}
            />
            <DefaultImageView
              source="images/common/close.png"
              onClick={closeModal}
              style={{
                width: minmax(30),
                height: minmax(30),
                margin: minmax(15),
                cursor: 'pointer',
              }}
            />
          </ModalHeader>

          <InfoRow>
            <DefaultText
              text={`${entityLabel}명: ${selectItem?.name || '-'}`}
              textStyle={{fontSize: minmax(20), color: colors.darkGray}}
            />
          </InfoRow>

          <LabelInput
            type={'text'}
            labelText={'사업자등록번호'}
            value={bizRegNumber}
            setValue={setBizRegNumber}
          />

          <DefaultButton
            text="change"
            actived={1}
            buttonStyle={{
              width: '100%',
              backgroundColor: colors.primary2,
            }}
            textStyle={{fontSize: minmax(25)}}
            onClick={() => {
              const trimmed = bizRegNumber?.trim() || '';
              if (!trimmed) {
                setErrorModal({title: '입력 오류', detail: '사업자등록번호를 입력해주세요.'});
                return;
              }
              const digitsOnly = trimmed.replace(/-/g, '');
              if (!/^\d{10}$/.test(digitsOnly)) {
                setErrorModal({
                  title: '입력 오류',
                  detail: '사업자등록번호는 10자리 숫자여야 합니다. (예: 1234567890 또는 123-45-67890)',
                });
                return;
              }
              setConfirmModal(true);
            }}
          />
        </ModalContainer>
      </CommonModal>
      {confirmModal && (
        <CommonModal
          title="사업자등록번호 변경 확인"
          detail={`사업자등록번호를 "${bizRegNumber.trim()}"(으)로 변경하시겠습니까?`}
          firstButtonText="confirm"
          secondButtonText="cancel"
          firstButtonClick={async () => {
            setConfirmModal(false);
            const result = await changeBusinessRegisterNumber({
              userInfo,
              setIsLoading,
              setErrorModal,
              entityType,
              entityUUID: selectItem?.uuid,
              businessRegisterNumber: bizRegNumber.trim(),
            });
            if (result) setCompleteModal(true);
          }}
          secondButtonClick={() => setConfirmModal(false)}
          closeModal={() => setConfirmModal(false)}
        />
      )}
      {completeModal && (
        <CommonModal
          title="사업자등록번호 변경 완료!"
          detail="사업자등록번호가 변경되었습니다."
          firstButtonText="confirm"
          firstButtonClick={() => {
            closeModal();
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default ChangeBusinessRegisterNumberModal;

const ModalContainer = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${minmax(20)};
`;

const ModalHeader = styled.div`
  width: 100%;
  margin-bottom: ${minmax(10)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const InfoRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;
